import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { SettingsService } from './settings.service';
import {
  CatalogFilter,
  CatalogObject,
  Category,
  CategoryContainer,
  Dictionaries,
} from '@shared/types/sketch-catalog';

@Injectable({ providedIn: 'root' })
export class CatalogService {
  constructor(
    private http: HttpClient,
    private settingsService: SettingsService,
  ) {}

  public getCatalog(
    params: { [key: string]: string | number | boolean } = null,
  ): Observable<CatalogObject[]> {
    return this.http
      .get<{
        success: boolean;
        items: CatalogObject[];
      }>(`${this.settingsService.v2}client/sketch/material/get-all`, { params: params })
      .pipe(
        map((res) => res?.items || []),
        catchError(() => of(null)),
      );
  }

  public getCategories(): Observable<Category[]> {
    return this.http
      .get<{
        success: boolean;
        items: CategoryContainer[];
      }>(`${this.settingsService.v2}client/sketch/material/get-categories`)
      .pipe(
        map((res) => {
          const seenIds = new Set<number>();
          const result: Category[] = [];

          const addUnique = (category: Category) => {
            if (category && !seenIds.has(category.id)) {
              seenIds.add(category.id);
              result.push(category);
            }
          };

          // parent — цепочка произвольной глубины (CategoryContainer
          // рекурсивный). Несколько items/предков могут ссылаться на один и
          // тот же CategoryContainer — добавляем каждого предка один раз,
          // от самого верхнего к текущему.
          const addContainer = (container: CategoryContainer) => {
            if (!container) {
              return;
            }

            addContainer(container.parent);
            addUnique(container.category);
          };

          (res?.items ?? []).forEach((item) => addContainer(item));

          return result;
        }),
        catchError(() => of(null)),
      );
  }

  public getProductImage(imageUrl: string): Observable<Blob> {
    return this.http.get(`${this.settingsService.storage}${imageUrl}`, { responseType: 'blob' });
  }

  public getFilters(): Observable<CatalogFilter[]> {
    return this.http
      .get<CatalogFilter[]>(`${this.settingsService.v2}client/sketch/search/get-filters`)
      .pipe(catchError(() => of(null)));
  }

  // public getProjectObjects(projectId: number): Observable<CatalogObject[]> {
  //   return this.http
  //     .get<{
  //       success: boolean;
  //       items: CatalogObject[];
  //     }>(`${this.settingsService.v2}/client/sketch/project-objects/get`, {
  //       params: { project_id: projectId },
  //     })
  //     .pipe(
  //       map((res) => res?.items),
  //       catchError(() => of(null)),
  //     );
  // }

  public getCustomObjects(params: {
    [key: string]: string | number | boolean;
  }): Observable<CatalogObject[]> {
    return this.http
      .get<{
        success: boolean;
        items: CatalogObject[];
      }>(`${this.settingsService.v2}client/sketch/material/get-custom`, { params })
      .pipe(
        map((res) => res?.items),
        catchError(() => of(null)),
      );
  }

  public restoreCustomObject(params: {
    [key: string]: string | number | boolean;
  }): Observable<CatalogObject> {
    return this.http
      .post<{
        success: boolean;
        object: CatalogObject;
      }>(`${this.settingsService.v2}client/sketch/user-catalog/restore`, null, { params })
      .pipe(
        map((res) => res?.object),
        catchError(() => of(null)),
      );
  }

  public getDictionaries(): Observable<Dictionaries> {
    return this.http
      .get<Dictionaries>(`${this.settingsService.v2}client/sketch/search/get-dictionaries`)
      .pipe(catchError(() => of(null)));
  }
}
